// Trade presets: picking a trade seeds the rest of onboarding with what
// companies in that trade typically use. Everything it seeds stays editable in
// the steps that follow — the preset is a starting point, not a decision.
export const TRADES = {
  hvacr: {
    icon: "❄️",
    name: "HVAC / Refrigeration",
    description: "Cooling, heating, refrigeration",
    vocab: {
      workOrder: "Work Order",
      serviceCall: "Service Call",
      tech: "Technician",
      asset: "Equipment",
      truckStock: "Truck Stock",
      recommendation: "Recommendation",
    },
    workTypes: [
      "Preventive Maintenance",
      "Service Call",
      "Installation",
      "Start-up / Commissioning",
      "Inspection",
      "Emergency",
    ],
    forms: [
      "Preventive Maintenance Checklist",
      "Equipment Start-up",
      "Refrigerant Tracking Log",
      "Ice Machine PM",
      "Coil Cleaning Checklist",
    ],
    compliance: ["Refrigerant Report (EPA 608)"],
    certs: ["EPA 608 Universal", "OSHA 10/30"],
    usesNTE: true,
    permits: false,
  },
  electrical: {
    icon: "⚡",
    name: "Electrical",
    description: "Service, panels, controls, projects",
    vocab: {
      workOrder: "Work Order",
      serviceCall: "Trouble Call",
      tech: "Electrician",
      asset: "Panel / Circuit",
      truckStock: "Van Stock",
      recommendation: "Recommendation",
    },
    workTypes: [
      "Trouble Call",
      "Preventive Maintenance",
      "Installation / Project",
      "Testing & Inspection",
      "Generator Service",
      "Emergency",
    ],
    forms: [
      "Annual Panel PM (IR Scan)",
      "Megger Test Report",
      "Lock-out / Tag-out (LOTO)",
      "Energized Work Permit (NFPA 70E)",
      "Panel Schedule",
      "Generator PM",
      "Fire Alarm Inspection",
    ],
    compliance: [
      "Test & Inspection Reports",
      "Permit / AHJ Inspection Tracking",
      "Certified Payroll (public works)",
    ],
    certs: [
      "Journeyman License",
      "Master Electrician",
      "Apprentice Registration",
      "OSHA 10/30",
      "NFPA 70E",
    ],
    usesNTE: true,
    permits: true,
  },
  plumbing: {
    icon: "🚰",
    name: "Plumbing",
    description: "Service, drains, backflow",
    vocab: {
      workOrder: "Work Order",
      serviceCall: "Service Call",
      tech: "Plumber",
      asset: "Fixture / System",
      truckStock: "Van Stock",
      recommendation: "Recommendation",
    },
    workTypes: [
      "Service Call",
      "Drain Cleaning",
      "Backflow Testing",
      "Installation",
      "Preventive Maintenance",
      "Emergency",
    ],
    forms: [
      "Backflow Test Report",
      "Water Heater Startup",
      "Camera Inspection Report",
      "Annual Fixture PM",
    ],
    compliance: ["Backflow Certifications", "Permit Tracking"],
    certs: ["Journeyman License", "Master Plumber", "Backflow Tester Cert"],
    usesNTE: true,
    permits: true,
  },
  roofing: {
    icon: "🏠",
    name: "Roofing",
    description: "Repairs, replacement, inspections",
    vocab: {
      workOrder: "Job",
      serviceCall: "Repair Call",
      tech: "Crew Member",
      asset: "Roof Section",
      truckStock: "Materials",
      recommendation: "Recommendation",
    },
    workTypes: ["Repair", "Replacement", "Inspection", "Preventive Maintenance", "Emergency Leak"],
    forms: [
      "Roof Inspection Report",
      "Moisture Survey",
      "Warranty Registration",
      "Safety / Fall Protection Checklist",
    ],
    compliance: ["Permit Tracking", "Warranty Documents"],
    certs: ["Manufacturer Certifications", "OSHA Fall Protection"],
    usesNTE: false,
    permits: true,
  },
  cleaning: {
    icon: "🧹",
    name: "Cleaning Services",
    description: "Residential & commercial cleaning",
    vocab: {
      workOrder: "Job",
      serviceCall: "One-Time Clean",
      tech: "Cleaner",
      asset: "Area / Room",
      truckStock: "Supplies",
      recommendation: "Note for Client",
    },
    workTypes: [
      "Recurring Clean",
      "Deep Clean",
      "One-Time Clean",
      "Move-In / Move-Out",
      "Post-Construction",
    ],
    forms: [
      "Cleaning Checklist",
      "Deep Clean Checklist",
      "Move-Out Inspection",
      "Supply Restock List",
    ],
    compliance: ["Insurance / Bonding Docs"],
    certs: ["Bonding", "Background Check"],
    usesNTE: false,
    permits: false,
  },
  lawn: {
    icon: "🌱",
    name: "Lawn & Landscape",
    description: "Mowing, applications, irrigation",
    vocab: {
      workOrder: "Job",
      serviceCall: "Service Visit",
      tech: "Crew Member",
      asset: "Zone / Equipment",
      truckStock: "Materials",
      recommendation: "Recommendation",
    },
    workTypes: [
      "Mowing",
      "Fertilization / Application",
      "Irrigation Service",
      "Seasonal Cleanup",
      "Landscape Installation",
    ],
    forms: ["Application Record", "Irrigation Inspection", "Seasonal Cleanup Checklist"],
    compliance: ["Pesticide Applicator Logs"],
    certs: ["Pesticide Applicator License"],
    usesNTE: false,
    permits: false,
  },
  chimney: {
    icon: "🧱",
    name: "Chimney / Hearth",
    description: "Sweeps, inspections, repairs",
    vocab: {
      workOrder: "Work Order",
      serviceCall: "Sweep / Inspection",
      tech: "Sweep Technician",
      asset: "Flue / Appliance",
      truckStock: "Truck Stock",
      recommendation: "Recommendation",
    },
    workTypes: ["Sweep", "Inspection", "Repair", "Installation", "Chimney Cap / Liner"],
    forms: [
      "Level 1 Inspection (NFPA 211)",
      "Level 2 Inspection",
      "Sweep Report",
      "Photo Documentation",
    ],
    compliance: ["CSIA Inspection Reports"],
    certs: ["CSIA Certification"],
    usesNTE: false,
    permits: false,
  },
  catering: {
    icon: "🍽️",
    name: "Catering / Events",
    description: "Event food service",
    vocab: {
      workOrder: "Event",
      serviceCall: "Booking",
      tech: "Staff Member",
      asset: "Venue / Menu",
      truckStock: "Inventory",
      recommendation: "Client Note",
    },
    workTypes: ["Corporate Event", "Wedding", "Private Party", "Drop-Off Catering", "Tasting"],
    forms: ["Banquet Event Order (BEO)", "Prep Checklist", "Post-Event Report"],
    compliance: ["Health Permits", "Alcohol License"],
    certs: ["Food Handler Card", "ServSafe"],
    usesNTE: false,
    permits: false,
  },
  other: {
    icon: "🛠️",
    name: "Other Service Trade",
    description: "Tell us about your work",
    vocab: {
      workOrder: "Work Order",
      serviceCall: "Service Call",
      tech: "Technician",
      asset: "Asset",
      truckStock: "Truck Stock",
      recommendation: "Recommendation",
    },
    workTypes: ["Service Call", "Preventive Maintenance", "Installation", "Inspection", "Emergency"],
    forms: [],
    compliance: [],
    certs: [],
    usesNTE: true,
    permits: false,
  },
};

// The roles a service company usually runs on, beside the Administrator it
// already has. The field role is named from the company's own wording — an
// electrical shop hires Electricians, not Technicians — so it carries the vocab
// key it is renamed by rather than a fixed name.
export const ROLE_PRESETS = [
  { key: "tech", name: "Technician", description: "Runs assigned work in the field" },
  { key: "dispatcher", name: "Dispatcher", description: "Schedules work and assigns it to the field" },
  { key: "supervisor", name: "Supervisor", description: "Oversees crews and approves their work" },
  { key: "office", name: "Office Manager", description: "Customers, quotes, invoices, the day-to-day" },
  { key: "accounting", name: "Accounting", description: "Billing, payroll and reporting" },
  { key: "warehouse", name: "Warehouse / Parts", description: "Materials, purchase orders and stock" },
];

// The concepts Makani renames around a company. The order is the order they are
// asked in.
export const VOCAB_KEYS = [
  ["workOrder", "Work Order", "The unit of work you dispatch and bill"],
  ["serviceCall", "Service Call", "Unplanned / demand work"],
  ["tech", "Technician", "The people in the field"],
  ["asset", "Asset", "The thing being serviced"],
  ["truckStock", "Truck Stock", "Materials carried on vehicles"],
  ["recommendation", "Recommendation", "Future work your team flags in the field"],
];
